import { Injectable, inject } from '@angular/core';
import { AuthStateService } from '../../../core/services/auth-state.service';
import { MemberProfileApi, ProfileRequestError } from '../data-access/member-profile-api.service';
import { ProfileOperationStorage } from '../data-access/profile-operation-storage.service';
import { MemberProfile, ProfileCommand, ProfilePhase, ProfileOperation, SUCCESS_MESSAGES, validateProfileCommand } from '../domain/member-profile';
const UNKNOWN = '更新結果を確認できません。再保存せず、現在の会員情報を再取得し、パスワードや不明な点は再ログインまたは店舗で確認してください。';

@Injectable({ providedIn: 'root' })
export class MemberProfileStore {
  private readonly api = inject(MemberProfileApi);
  private readonly auth = inject(AuthStateService);
  private readonly receipts = inject(ProfileOperationStorage);
  private owner: number | null = null;
  private generation = 0;
  private readVersion = 0;
  private profileValue: MemberProfile | null = null;
  private operationValue: ProfileOperation = { phase: 'idle', message: '' };
  private loadingValue = false;
  private loadErrorValue = '';
  get profile(): Readonly<MemberProfile> | null { return this.profileValue; }
  get operation(): ProfileOperation { return this.operationValue; }
  get loading(): boolean { return this.loadingValue; }
  get loadError(): string { return this.loadErrorValue; }
  get busy(): boolean { return ['checking', 'saving', 'refreshing'].includes(this.operationValue.phase); }
  get blocked(): boolean { return this.busy || ['unknown', 'saved-stale'].includes(this.operationValue.phase); }
  private currentOwner(): number | null {
    const user = this.auth.currentMember;
    const id = Number(user?.['id'] ?? user?.memberId);
    return user?.token && Number.isInteger(id) && id > 0 ? id : null;
  }
  private current(generation: number): boolean { return generation === this.generation && this.owner === this.currentOwner(); }
  private state(phase: ProfilePhase, message: string, kind = this.operationValue.kind): void {
    this.operationValue = { phase, message, kind };
  }
  private clearReceipt(): void { if (this.owner !== null) this.receipts.clear(this.owner); }

  async enter(): Promise<void> {
    const owner = this.currentOwner();
    if (owner !== this.owner) {
      this.owner = owner; this.generation++; this.readVersion++;
      this.profileValue = null; this.loadingValue = false; this.loadErrorValue = '';
      this.operationValue = { phase: 'idle', message: '' };
      if (owner !== null) {
        try {
          const receipt = this.receipts.read(owner);
          if (receipt) this.state(receipt.outcome === 'confirmed' ? 'saved-stale' : 'unknown',
            receipt.outcome === 'confirmed' ? '更新は完了しています。表示を再取得してください。' : UNKNOWN, receipt.kind);
        } catch { this.state('unknown', '更新記録を確認できません。再保存せず、店舗で更新結果を確認してください。'); }
      }
    }
    if (!this.busy) await this.refresh();
  }

  async refresh(): Promise<void> {
    if (this.owner === null || this.owner !== this.currentOwner() || this.busy && this.operationValue.phase !== 'refreshing') return;
    const generation = this.generation, version = ++this.readVersion;
    this.loadingValue = true; this.loadErrorValue = '';
    try {
      const profile = await this.api.read();
      if (!this.current(generation) || version !== this.readVersion) return;
      if (profile.id !== this.owner) throw new ProfileRequestError('invalid-response');
      this.profileValue = profile;
      if (this.operationValue.phase === 'saved-stale') {
        this.clearReceipt(); this.state('saved', SUCCESS_MESSAGES[this.operationValue.kind!]);
      }
    } catch (error) {
      if (this.current(generation) && version === this.readVersion) this.loadErrorValue = this.message(error, '会員情報を取得できませんでした。表示を再取得してください。');
    } finally {
      if (this.current(generation) && version === this.readVersion) this.loadingValue = false;
    }
  }

  private message(error: unknown, fallback: string): string {
    if (error instanceof ProfileRequestError && error.reason === 'authentication') return '認証を確認できません。再ログインしてから操作してください。';
    if (error instanceof ProfileRequestError && error.reason === 'forbidden') return 'この操作を実行する権限がありません。店舗へお問い合わせください。';
    return fallback;
  }

  beginEdit(): void { if (!this.blocked) this.operationValue = { phase: 'idle', message: '' }; }

  async submit(command: ProfileCommand): Promise<boolean> {
    const member = this.profileValue;
    if (!member || this.blocked || this.owner !== this.currentOwner() || this.loadingValue || this.loadErrorValue) return false;
    const invalid = validateProfileCommand(command, member);
    if (invalid) { this.state('failed', invalid, command.kind); return false; }
    const generation = this.generation;
    let sent = false, confirmed = false;
    this.readVersion++; this.state('checking', '入力内容を確認しています。', command.kind);
    try {
      if (command.kind === 'email' || command.kind === 'loginId') {
        const exists = await this.api.exists(command.kind, command.value);
        if (!this.current(generation)) return false;
        if (exists) { this.state('failed', `すでに使用されている${command.kind === 'email' ? 'メールアドレス' : 'ログインID'}です`); return false; }
      }
      if (command.kind === 'password') {
        const valid = await this.api.checkPassword(member.loginId, command.current);
        if (!this.current(generation)) return false;
        if (!valid) { this.state('failed', '現在のパスワードを再度入力してください'); return false; }
      }
      if (!this.current(generation)) return false;
      // Persist only the operation receipt, never the editing values or passwords.
      this.receipts.write({ owner: member.id, kind: command.kind, outcome: 'pending' });
      this.state('saving', '保存しています。'); sent = true;
      await this.api.update(member.id, command); confirmed = true;
      this.receipts.write({ owner: member.id, kind: command.kind, outcome: 'confirmed' });
      if (!this.current(generation)) return false;
      this.state('refreshing', '更新が完了しました。表示を再取得しています。');
      await this.refresh();
      if (!this.current(generation)) return false;
      if (this.loadErrorValue) this.state('saved-stale', '更新は完了していますが、表示を取得できませんでした。再保存せず、表示を再取得してください。');
      else { this.clearReceipt(); this.state('saved', SUCCESS_MESSAGES[command.kind]); }
      return true;
    } catch (error) {
      if (!this.current(generation)) return false;
      if (confirmed) { this.state('saved-stale', '更新は完了しています。表示と更新記録を再確認してください。'); return true; }
      const rejectedBeforeUpdate = error instanceof ProfileRequestError && ['authentication', 'forbidden'].includes(error.reason);
      if (sent && !rejectedBeforeUpdate) {
        this.state('unknown', UNKNOWN);
        try { this.receipts.write({ owner: member.id, kind: command.kind, outcome: 'unknown' }); } catch { /* Pending receipt still protects reload. */ }
      } else {
        if (sent) { try { this.clearReceipt(); } catch { /* Retain the conservative receipt. */ } }
        this.state('failed', this.message(error, sent ? '保存できませんでした。' : '確認処理を完了できませんでした。入力を保持しています。ブラウザの保存機能・通信を確認して再度お試しください。'));
      }
      return false;
    } finally {
      if (command.kind === 'password') command.current = command.next = command.confirmation = '';
    }
  }

  acknowledgeOutcome(): void {
    if (this.operationValue.phase !== 'unknown' || this.loadingValue || this.owner !== this.currentOwner()) return;
    try { this.clearReceipt(); this.state('idle', '更新結果の確認を受け付けました。編集は改めて開始してください。'); }
    catch { this.state('unknown', '更新記録を解除できません。ブラウザの保存機能を確認してください。'); }
  }
}
